import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { kes, ltr } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { VarianceBadge } from "@/components/StatusBadge";
import { toast } from "sonner";
import { Loader2, Gauge, Receipt, Wallet, Smartphone, TrendingDown, FileSpreadsheet, Truck, FileText, Package } from "lucide-react";

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}
function monthStartISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

export default function Reports() {
  const [dateFrom, setDateFrom] = useState(monthStartISO());
  const [dateTo, setDateTo] = useState(todayISO());
  const [tab, setTab] = useState("wetstock");
  const [loading, setLoading] = useState(false);
  const [wetstock, setWetstock] = useState(null);
  const [tills, setTills] = useState(null);
  const [expenses, setExpenses] = useState(null);
  const [methodId, setMethodId] = useState("all");
  const [payments, setPayments] = useState(null);
  const [shorts, setShorts] = useState(null);
  const [deliveries, setDeliveries] = useState(null);
  const [dryStock, setDryStock] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [pdfTankId, setPdfTankId] = useState("all");

  const REPORT_ENDPOINTS = {
    wetstock: "wetstock", tills: "tills", expenses: "expenses", payments: "payments", shorts: "shorts", deliveries: "deliveries",
    dryStock: "dry-stock",
  };

  const downloadExcel = async () => {
    setDownloading(true);
    try {
      const endpoint = REPORT_ENDPOINTS[tab];
      const params = { date_from: dateFrom, date_to: dateTo, ...(tab === "payments" && methodId !== "all" ? { method_id: methodId } : {}) };
      const res = await api.get(`/reports/period/${endpoint}/excel`, { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${endpoint}_${dateFrom}_to_${dateTo}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Export failed");
    } finally {
      setDownloading(false);
    }
  };

  const downloadWetstockPdf = async (tankId) => {
    setDownloadingPdf(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo, ...(tankId ? { tank_id: tankId } : {}) };
      const res = await api.get("/reports/period/tank-reconciliation/pdf", { params, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `wetstock_reconciliation_${dateFrom}_to_${dateTo}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("PDF export failed");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [w, ti, e, p, sh, dl, ds] = await Promise.all([
        api.get("/reports/period/wetstock", { params: { date_from: dateFrom, date_to: dateTo } }),
        api.get("/reports/period/tills", { params: { date_from: dateFrom, date_to: dateTo } }),
        api.get("/reports/period/expenses", { params: { date_from: dateFrom, date_to: dateTo } }),
        api.get("/reports/period/payments", { params: { date_from: dateFrom, date_to: dateTo, ...(methodId !== "all" ? { method_id: methodId } : {}) } }),
        api.get("/reports/period/shorts", { params: { date_from: dateFrom, date_to: dateTo } }),
        api.get("/reports/period/deliveries", { params: { date_from: dateFrom, date_to: dateTo } }),
        api.get("/reports/period/dry-stock", { params: { date_from: dateFrom, date_to: dateTo } }),
      ]);
      setWetstock(w.data);
      setTills(ti.data);
      setExpenses(e.data);
      setPayments(p.data);
      setShorts(sh.data);
      setDeliveries(dl.data);
      setDryStock(ds.data);
    } catch {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, methodId]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Reports</h1>
        <p className="text-sm text-muted-foreground">Rolled up from shift records — the shift is always the source document.</p>
      </div>

      <Card className="flex flex-wrap items-end gap-4 p-4">
        <div className="space-y-1.5">
          <Label className="text-xs">From</Label>
          <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="report-date-from" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">To</Label>
          <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="report-date-to" />
        </div>
        <Button onClick={load} disabled={loading} data-testid="report-refresh-button">
          {loading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : null}Refresh
        </Button>
        <Button variant="outline" onClick={downloadExcel} disabled={downloading} data-testid="report-export-excel-button">
          {downloading ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileSpreadsheet className="mr-1.5 h-4 w-4" />}Export Excel
        </Button>
        {tab === "wetstock" && (
          <>
            <div className="space-y-1.5">
              <Label className="text-xs">Tank</Label>
              <Select value={pdfTankId} onValueChange={setPdfTankId}>
                <SelectTrigger className="w-48" data-testid="report-wetstock-tank-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tanks</SelectItem>
                  {(wetstock?.tanks || []).map((t) => <SelectItem key={t.tank_id} value={t.tank_id}>{t.tank_label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Button variant="outline" onClick={() => downloadWetstockPdf(pdfTankId === "all" ? null : pdfTankId)} disabled={downloadingPdf} data-testid="report-wetstock-pdf-button">
              {downloadingPdf ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <FileText className="mr-1.5 h-4 w-4" />}Wetstock Reconciliation (PDF)
            </Button>
          </>
        )}
      </Card>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="wetstock" data-testid="tab-wetstock"><Gauge className="mr-1.5 h-4 w-4" />Wetstock</TabsTrigger>
          <TabsTrigger value="tills" data-testid="tab-tills"><Smartphone className="mr-1.5 h-4 w-4" />M-Pesa Tills</TabsTrigger>
          <TabsTrigger value="expenses" data-testid="tab-expenses"><Receipt className="mr-1.5 h-4 w-4" />Expenses</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments"><Wallet className="mr-1.5 h-4 w-4" />Payment Reconciliation</TabsTrigger>
          <TabsTrigger value="shorts" data-testid="tab-shorts"><TrendingDown className="mr-1.5 h-4 w-4" />Attendant Shorts</TabsTrigger>
          <TabsTrigger value="deliveries" data-testid="tab-deliveries"><Truck className="mr-1.5 h-4 w-4" />Deliveries</TabsTrigger>
          <TabsTrigger value="dryStock" data-testid="tab-dry-stock"><Package className="mr-1.5 h-4 w-4" />Dry Stock</TabsTrigger>
        </TabsList>

        <TabsContent value="wetstock" className="mt-4">
          <WetstockTab data={wetstock} onDownloadPdf={downloadWetstockPdf} downloadingPdf={downloadingPdf} />
        </TabsContent>
        <TabsContent value="tills" className="mt-4">
          <TillsTab data={tills} />
        </TabsContent>
        <TabsContent value="expenses" className="mt-4">
          <ExpensesTab data={expenses} />
        </TabsContent>
        <TabsContent value="payments" className="mt-4">
          <PaymentsTab data={payments} methodId={methodId} setMethodId={setMethodId} />
        </TabsContent>
        <TabsContent value="shorts" className="mt-4">
          <ShortsTab data={shorts} />
        </TabsContent>
        <TabsContent value="deliveries" className="mt-4">
          <DeliveriesTab data={deliveries} />
        </TabsContent>
        <TabsContent value="dryStock" className="mt-4">
          <DryStockTab data={dryStock} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function WetstockTab({ data, onDownloadPdf, downloadingPdf }) {
  if (!data) return null;
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="recon-table">
          <thead>
            <tr>
              <th className="text-left">Tank</th>
              <th>Opening Dip</th><th>Deliveries</th><th>RTT</th><th>Pump Test</th><th>Closing Dip</th>
              <th>Dip Sales (L)</th><th>Pump Sales (L)</th><th>Variance (L)</th><th>Var %</th><th className="text-left">Status</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody>
            {data.tanks.length === 0 && (
              <tr><td colSpan={12} className="py-6 text-center text-sm text-muted-foreground">No tank data in this range.</td></tr>
            )}
            {data.tanks.map((t) => (
              <tr key={t.tank_id} data-testid={`wetstock-row-${t.tank_id}`}>
                <td className="!text-left font-medium">{t.tank_label} <span className="text-xs text-muted-foreground">({t.product_name})</span></td>
                <td className="calc-cell">{ltr(t.opening_dip)}</td>
                <td className="calc-cell">{ltr(t.deliveries_total)}</td>
                <td className="calc-cell">{ltr(t.rtt_total)}</td>
                <td className="calc-cell text-muted-foreground" title="Returned to tank same shift — already reflected in Closing Dip, shown for visibility only">{ltr(t.pump_test_total)}</td>
                <td className="calc-cell">{ltr(t.closing_dip)}</td>
                <td className="calc-cell">{ltr(t.dip_sales_total)}</td>
                <td className="calc-cell">{ltr(t.pump_throughput_total)}</td>
                <td className="calc-cell">{ltr(t.variance_l)}</td>
                <td className="calc-cell">{ltr(t.variance_pct)}%</td>
                <td className="!text-left"><VarianceBadge status={t.status}>{t.status}</VarianceBadge></td>
                <td className="text-center">
                  <button
                    onClick={() => onDownloadPdf(t.tank_id)}
                    disabled={downloadingPdf}
                    className="text-slate-400 hover:text-slate-700 disabled:opacity-50"
                    title="Download Wet Stock Reconciliation Report (PDF)"
                    data-testid={`wetstock-pdf-button-${t.tank_id}`}
                  >
                    <FileText className="h-4 w-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function TillsTab({ data }) {
  if (!data) return null;
  return (
    <Card className="overflow-hidden">
      <div className="overflow-x-auto">
        <table className="recon-table">
          <thead>
            <tr>
              <th className="text-left">Till</th>
              <th>Opening Balance</th><th>M-Pesa Received</th><th>Withdrawals</th><th>Closing Balance</th>
              <th>Latest Variance</th><th className="text-left">Status</th>
            </tr>
          </thead>
          <tbody>
            {data.tills.length === 0 && (
              <tr><td colSpan={7} className="py-6 text-center text-sm text-muted-foreground">No till data in this range.</td></tr>
            )}
            {data.tills.map((t) => (
              <tr key={t.till_id} data-testid={`till-row-${t.till_id}`}>
                <td className="!text-left font-medium">{t.till_label} <span className="text-xs text-muted-foreground">({t.till_number})</span></td>
                <td className="calc-cell">{kes(t.opening_balance)}</td>
                <td className="calc-cell">{kes(t.mpesa_received_total)}</td>
                <td className="calc-cell">{kes(t.withdrawal_total)}</td>
                <td className="calc-cell font-semibold">{kes(t.closing_balance)}</td>
                <td className="calc-cell">{t.last_variance === null || t.last_variance === undefined ? "—" : `${t.last_variance >= 0 ? "+" : ""}${kes(t.last_variance)}`}</td>
                <td className="!text-left">{t.last_status ? <VarianceBadge status={t.last_status}>{t.last_status}</VarianceBadge> : <span className="text-xs text-muted-foreground">—</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function ExpensesTab({ data }) {
  if (!data) return null;
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card className="overflow-hidden lg:col-span-2">
        <div className="overflow-x-auto">
          <table className="recon-table">
            <thead>
              <tr><th className="text-left">Date</th><th className="text-left">Period</th><th className="text-left">Category</th><th className="text-left">Description</th><th className="text-left">Voucher</th><th>Amount (KES)</th></tr>
            </thead>
            <tbody>
              {data.lines.length === 0 && (
                <tr><td colSpan={6} className="py-6 text-center text-sm text-muted-foreground">No expenses in this range.</td></tr>
              )}
              {data.lines.map((l, i) => (
                <tr key={i}>
                  <td className="!text-left">{l.date}</td>
                  <td className="!text-left">{l.shift_type}</td>
                  <td className="!text-left">{l.category}</td>
                  <td className="!text-left">{l.description}</td>
                  <td className="!text-left">{l.voucher_no}</td>
                  <td className="calc-cell">{kes(l.amount)}</td>
                </tr>
              ))}
              <tr className="font-semibold"><td className="!text-left" colSpan={5}>Total</td><td className="calc-cell">{kes(data.total)}</td></tr>
            </tbody>
          </table>
        </div>
      </Card>
      <Card className="p-4">
        <h4 className="mb-3 font-display font-semibold text-slate-800">By Category</h4>
        <div className="space-y-2 text-sm">
          {Object.entries(data.by_category).length === 0 && <p className="text-muted-foreground">—</p>}
          {Object.entries(data.by_category).map(([cat, amt]) => (
            <div key={cat} className="flex items-center justify-between">
              <span className="text-muted-foreground">{cat}</span>
              <span className="font-mono-fin font-semibold">{kes(amt)}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function PaymentsTab({ data, methodId, setMethodId }) {
  if (!data) return null;
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-3">
        <Label className="text-xs">Payment Method</Label>
        <Select value={methodId} onValueChange={setMethodId}>
          <SelectTrigger className="w-56" data-testid="report-method-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Methods</SelectItem>
            {data.payment_methods.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Lump Total</div><div className="mt-1 font-mono-fin font-bold">KSh {kes(data.lump_total)}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Itemized Total</div><div className="mt-1 font-mono-fin font-bold">KSh {kes(data.itemized_total)}</div></Card>
        <Card className="p-3"><div className="text-[10px] uppercase tracking-wider text-muted-foreground">Unmatched Variance</div><div className={`mt-1 font-mono-fin font-bold ${Math.abs(data.unmatched_variance) < 0.01 ? "text-emerald-600" : "text-rose-600"}`}>{kes(data.unmatched_variance)}</div></Card>
      </div>

      <Card className="overflow-hidden">
        <div className="border-b bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-800">Per-Shift Reconciliation</div>
        <div className="overflow-x-auto">
          <table className="recon-table">
            <thead>
              <tr><th className="text-left">Date</th><th className="text-left">Period</th><th className="text-left">Attendant</th><th className="text-left">Method</th><th>Lump (KES)</th><th>Itemized (KES)</th><th>Variance</th><th className="text-left">Status</th></tr>
            </thead>
            <tbody>
              {data.shift_rows.length === 0 && (
                <tr><td colSpan={8} className="py-6 text-center text-sm text-muted-foreground">No payments in this range.</td></tr>
              )}
              {data.shift_rows.map((r, i) => (
                <tr key={i}>
                  <td className="!text-left">{r.date}</td>
                  <td className="!text-left">{r.shift_type}</td>
                  <td className="!text-left">{r.attendant_name}</td>
                  <td className="!text-left">{r.method_name}</td>
                  <td className="calc-cell">{kes(r.lump_amount)}</td>
                  <td className="calc-cell">{kes(r.itemized_total)}</td>
                  <td className="calc-cell">{r.variance >= 0 ? "+" : ""}{kes(r.variance)}</td>
                  <td className="!text-left">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r.matched ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                      {r.matched ? "Matched" : "Unmatched"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="border-b bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-800">Itemized Transactions</div>
        <div className="overflow-x-auto">
          <table className="recon-table">
            <thead>
              <tr><th className="text-left">Date</th><th className="text-left">Period</th><th className="text-left">Attendant</th><th className="text-left">Method</th><th className="text-left">Reference</th><th className="text-left">Note</th><th>Amount (KES)</th></tr>
            </thead>
            <tbody>
              {data.transactions.length === 0 && (
                <tr><td colSpan={7} className="py-6 text-center text-sm text-muted-foreground">No itemized transactions in this range.</td></tr>
              )}
              {data.transactions.map((t, i) => (
                <tr key={i}>
                  <td className="!text-left">{t.date}</td>
                  <td className="!text-left">{t.shift_type}</td>
                  <td className="!text-left">{t.attendant_name}</td>
                  <td className="!text-left">{t.method_name}</td>
                  <td className="!text-left">{t.reference}</td>
                  <td className="!text-left">{t.note}</td>
                  <td className="calc-cell">{kes(t.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function DeliveriesTab({ data }) {
  if (!data) return null;
  return (
    <div className="grid gap-4 lg:grid-cols-3">
      <Card className="overflow-hidden lg:col-span-2">
        <div className="overflow-x-auto">
          <table className="recon-table">
            <thead>
              <tr>
                <th className="text-left">Shift Date</th><th className="text-left">Tank</th><th className="text-left">Invoice No.</th>
                <th className="text-left">Invoice Date</th><th>Invoice Amount (KES)</th><th>Invoice Volume (L)</th>
                <th>Offloaded Qty (L)</th><th>Variance (L)</th><th>Var %</th><th className="text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.lines.length === 0 && (
                <tr><td colSpan={10} className="py-6 text-center text-sm text-muted-foreground">No deliveries in this range.</td></tr>
              )}
              {data.lines.map((l, i) => (
                <tr key={i} data-testid={`delivery-row-${i}`}>
                  <td className="!text-left">{l.date} <span className="text-xs text-muted-foreground">({l.shift_type})</span></td>
                  <td className="!text-left font-medium">{l.tank_label} <span className="text-xs text-muted-foreground">({l.product_name})</span></td>
                  <td className="!text-left">{l.invoice_no}</td>
                  <td className="!text-left">{l.invoice_date}</td>
                  <td className="calc-cell">{kes(l.invoice_amount)}</td>
                  <td className="calc-cell">{ltr(l.invoice_volume)}</td>
                  <td className="calc-cell">{ltr(l.offloaded_quantity)}</td>
                  <td className="calc-cell">{l.variance >= 0 ? "+" : ""}{ltr(l.variance)}</td>
                  <td className="calc-cell">{ltr(l.variance_pct)}%</td>
                  <td className="!text-left"><VarianceBadge status={l.status}>{l.status}</VarianceBadge></td>
                </tr>
              ))}
              <tr className="font-semibold">
                <td className="!text-left" colSpan={4}>Grand Total</td>
                <td className="calc-cell">{kes(data.grand_invoice_amount)}</td>
                <td className="calc-cell">{ltr(data.grand_invoice_volume)}</td>
                <td className="calc-cell">{ltr(data.grand_offloaded_quantity)}</td>
                <td className="calc-cell" colSpan={3}>—</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
      <Card className="overflow-hidden">
        <div className="border-b bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800">Total per Tank</div>
        <div className="p-4 space-y-3 text-sm">
          {data.totals.length === 0 && <p className="text-muted-foreground">—</p>}
          {data.totals.map((t) => (
            <div key={t.tank_label} className="space-y-0.5" data-testid={`delivery-total-${t.tank_label}`}>
              <div className="font-medium text-slate-800">{t.tank_label}</div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Invoiced</span><span className="font-mono-fin">{ltr(t.invoice_volume)} L</span>
              </div>
              <div className="flex items-center justify-between text-xs text-muted-foreground">
                <span>Offloaded</span><span className="font-mono-fin">{ltr(t.offloaded_quantity)} L</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Variance</span>
                <span className={`font-mono-fin font-semibold ${Math.abs(t.variance) > 1 ? "text-rose-600" : ""}`}>{t.variance >= 0 ? "+" : ""}{ltr(t.variance)} L</span>
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function DryStockTab({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <div className="border-b bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800">Stock Movement by SKU</div>
        <div className="overflow-x-auto">
          <table className="recon-table">
            <thead>
              <tr>
                <th className="text-left">Item</th><th className="text-left">Category</th>
                <th>Opening</th><th>Received</th><th>Sold</th><th>Closing</th>
                <th>Revenue (KES)</th><th>Gross Profit (KES)</th>
              </tr>
            </thead>
            <tbody>
              {data.items.length === 0 && (
                <tr><td colSpan={8} className="py-6 text-center text-sm text-muted-foreground">No dry stock data in this range.</td></tr>
              )}
              {data.items.map((i) => (
                <tr key={i.item_id} data-testid={`dry-stock-row-${i.item_id}`}>
                  <td className="!text-left font-medium">{i.item_name}</td>
                  <td className="!text-left text-muted-foreground">{i.category}</td>
                  <td className="calc-cell">{ltr(i.opening, 0)}</td>
                  <td className="calc-cell">{ltr(i.received_total, 0)}</td>
                  <td className="calc-cell">{ltr(i.sold_total, 0)}</td>
                  <td className="calc-cell">{ltr(i.closing, 0)}</td>
                  <td className="calc-cell">{kes(i.revenue_total)}</td>
                  <td className="calc-cell">{kes(i.gross_profit_total)}</td>
                </tr>
              ))}
              <tr className="font-semibold">
                <td className="!text-left" colSpan={6}>Total</td>
                <td className="calc-cell">{kes(data.grand_revenue)}</td>
                <td className="calc-cell">{kes(data.grand_gross_profit)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      <Card className="overflow-hidden">
        <div className="border-b bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800">Deliveries</div>
        <div className="overflow-x-auto">
          <table className="recon-table">
            <thead>
              <tr>
                <th className="text-left">Date</th><th className="text-left">Item</th><th className="text-left">Invoice No.</th>
                <th>Invoice Amount (KES)</th><th>Invoice Qty</th><th>Actual Qty</th><th>Variance</th><th>Var %</th><th className="text-left">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.delivery_lines.length === 0 && (
                <tr><td colSpan={9} className="py-6 text-center text-sm text-muted-foreground">No deliveries in this range.</td></tr>
              )}
              {data.delivery_lines.map((d, i) => (
                <tr key={i} data-testid={`dry-stock-delivery-row-${i}`}>
                  <td className="!text-left">{d.date}</td>
                  <td className="!text-left font-medium">{d.item_name}</td>
                  <td className="!text-left">{d.invoice_no}</td>
                  <td className="calc-cell">{kes(d.invoice_amount)}</td>
                  <td className="calc-cell">{ltr(d.invoice_quantity, 0)}</td>
                  <td className="calc-cell">{ltr(d.actual_quantity, 0)}</td>
                  <td className="calc-cell">{d.variance >= 0 ? "+" : ""}{ltr(d.variance, 0)}</td>
                  <td className="calc-cell">{ltr(d.variance_pct)}%</td>
                  <td className="!text-left"><VarianceBadge status={d.status}>{d.status}</VarianceBadge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

function ShortsTab({ data }) {
  if (!data) return null;
  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="text-xs uppercase tracking-wider text-muted-foreground">Total Shorts (for payroll deduction)</div>
        <div className="mt-1 font-mono-fin text-2xl font-bold text-rose-600">KSh {kes(data.grand_total)}</div>
        <p className="mt-1 text-xs text-muted-foreground">Only real shortfalls beyond the cash tolerance (KSh {kes(data.cash_tolerance)}) count — overages aren't deducted.</p>
      </Card>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="overflow-hidden lg:col-span-2">
          <div className="border-b bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800">Shortfalls by Shift</div>
          <div className="overflow-x-auto">
            <table className="recon-table">
              <thead>
                <tr><th className="text-left">Date</th><th className="text-left">Period</th><th className="text-left">Attendant</th><th className="text-left">Source</th><th>Short Amount</th></tr>
              </thead>
              <tbody>
                {data.lines.length === 0 && (
                  <tr><td colSpan={5} className="py-6 text-center text-sm text-muted-foreground">No shortfalls beyond tolerance in this range.</td></tr>
                )}
                {data.lines.map((l, i) => (
                  <tr key={i} data-testid={`short-row-${i}`}>
                    <td className="!text-left">{l.date}</td>
                    <td className="!text-left">{l.shift_type}</td>
                    <td className="!text-left">{l.attendant_name}</td>
                    <td className="!text-left">{l.source}</td>
                    <td className="calc-cell text-rose-600">{kes(l.short_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        <Card className="overflow-hidden">
          <div className="border-b bg-slate-50 px-4 py-3 text-sm font-semibold text-slate-800">Total per Attendant</div>
          <div className="p-4 space-y-2 text-sm">
            {data.totals.length === 0 && <p className="text-muted-foreground">—</p>}
            {data.totals.map((t) => (
              <div key={t.attendant_name} className="flex items-center justify-between" data-testid={`short-total-${t.attendant_name}`}>
                <span className="text-muted-foreground">{t.attendant_name}</span>
                <span className="font-mono-fin font-semibold text-rose-600">KSh {kes(t.total_short)}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
